import React from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap-5';
import Select from 'react-select';

import PluginFilterType from '../types/plugin_filter_type';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { capitalize, concat, includes, map, startCase, toLower, without } from 'lodash';

export default class FilterEditModal extends React.Component {
  static propTypes = {
    fieldConfig: PropTypes.object.isRequired,
    filterFieldOptions: PropTypes.object.isRequired,
    filterModel: PluginFilterType,
    propertyTypes: PropTypes.object.isRequired,
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired
  };

  componentDidUpdate() {
    const defaultOptions = this.props.fieldConfig.default;
    const selectedDefault = this.props.filterModel.defaultValue;

    if (defaultOptions && !defaultOptions[selectedDefault]) {
      this.props.onUpdate('defaultValue', Object.keys(defaultOptions)[0]);
    }
  }

  handleUpdate = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onUpdate(target.name, value);
  }

  handlePropertyTypeUpdate = (newValues) => {
    this.handleUpdate({
      target: {
        name: 'propertyTypes',
        value: newValues.map(option => option.value)
      }
    });
  }

  propertyTypeValues() {
    return this.props.filterModel.propertyTypes.map((value) => {
      return { value: value, label: this.props.propertyTypes[value] };
    });
  }

  propertyTypeOptions() {
    return map(this.props.propertyTypes, (v, k) => {
      return { value: k, label: v };
    });
  }

  propertyTypeLabel() {
    if (this.props.filterModel.propertyTypes.length === 0) {
      return 'This filter will display for any property type.';
    } else if (this.props.filterModel.propertyTypes.length === 1) {
      return (
        <span>
          This filter will display when the 'Property Type' filter is present and
          '{this.props.propertyTypes[this.props.filterModel.propertyTypes[0]]}' is selected.
        </span>
      );
    } else {
      const types = this.props.filterModel.propertyTypes.map(value => this.props.propertyTypes[value]).join(' / ');
      return (
        <span>
          This filter will display when the 'Property Type' filter is present and any of '{types}' are selected.
        </span>
      );
    }
  }

  singleSelectLabel(key, label) {
    const upperLabel = startCase(toLower(label));

    if (!this.props.filterModel[key]) {
      return `This filter will display for any ${upperLabel}.`;
    } else {
      return (
        <span>
          This filter will display when the '{upperLabel}' filter is present and
          'For {capitalize(this.props.filterModel[key])}' is selected.
        </span>
      );
    }
  }

  isAddtionalOptionPresetSelected(filterField) {
    return includes(this.props.filterModel.additionalOptionsPreset, filterField);
  }

  isPropertyTypeUseFilterSelected(filterField) {
    return includes(this.props.filterModel.propertyTypeUseFilters, filterField);
  }

  showStatusCondition() {
    return this.props.filterModel.filterField != 'status' && !this.props.fieldConfig.status;
  }

  showPropertyTypeCondition() {
    return this.props.filterModel.filterField != 'property_type';
  }

  handleAddtionalOptionPresetUpdate = (checked, filterField) => {
    this.updateListField('additionalOptionsPreset', checked, filterField);
  }

  handleAPropertyTypeUseFilterUpdate = (checked, filterField) => {
    this.updateListField('propertyTypeUseFilters', checked, filterField);
  }

  updateListField = (fieldName, checked, filterField) => {
    const existingPresets = this.props.filterModel[fieldName];

    if (checked) {
      this.props.onUpdate(fieldName, concat(existingPresets, filterField));
    } else {
      this.props.onUpdate(fieldName, without(existingPresets, filterField));
    }
  }

  allowAllSelectedLabel = () => {
    const filterField = this.props.filterFieldOptions[this.props.filterModel.filterField];

    return (filterField || {}).allowAllSelectedLabel;
  }

  allSelectedLabelInfoIcon = () => {
    return (
      <OverlayTrigger
        overlay={<Tooltip id="all-selected-label-info">
          This will be the value shown when all options are selected
        </Tooltip>}
        placement="right"
      >
        <FontAwesomeIcon className="ms-2" icon={faInfoCircle} />
      </OverlayTrigger>
    );
  }

  render() {
    const idPrefix = `filter-edit-${this.props.filterModel.filterField || 'filter'}`;

    return (
      <Modal
        className="filter-edit-modal"
        show={this.props.show}
        onHide={this.props.onClose}
      >
        <Modal.Header closeButton>
          <Modal.Title as="h4">Edit Search Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6 className="mt-0 mb-4">Select Filter</h6>
          <p>Select which filter you want to add to your search bar</p>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Select
                  name="filterField"
                  value={this.props.filterModel.filterField}
                  onChange={this.handleUpdate}
                >
                  {Object.keys(this.props.filterFieldOptions).map((key) => {
                    return <option key={key} value={key}>{this.props.filterFieldOptions[key].label}</option>;
                  })}
                </Form.Select>
                {this.props.fieldConfig.helpText &&
                  <Form.Text className="text-muted" name="help-block">{ this.props.fieldConfig.helpText }</Form.Text>
                }
              </Form.Group>
            </Col>
          </Row>
          {this.props.fieldConfig.default &&
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Default Value</Form.Label>
                  <Form.Select
                    name="defaultValue"
                    value={this.props.filterModel.defaultValue}
                    onChange={this.handleUpdate}
                  >
                    {Object.keys(this.props.fieldConfig.default).map((k) => {
                      return <option key={k} value={k}>{ this.props.fieldConfig.default[k] }</option>;
                    })}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          }
          {this.props.fieldConfig.allowMetric &&
            <Form.Check
              checked={this.props.filterModel.metric}
              className="mb-3"
              id={`${idPrefix}-metric`}
              label="Use Metric Units?"
              name="metric"
              type="checkbox"
              onChange={this.handleUpdate}
            />
          }
          {this.props.fieldConfig.additionalOptionsPresets &&
            <div className="mb-3">
              {map(this.props.fieldConfig.additionalOptionsPresets, (label, key) => {
                return (
                  <Form.Check
                    checked={this.isAddtionalOptionPresetSelected(key)}
                    id={`${idPrefix}-additional-${key}`}
                    key={key}
                    label={label}
                    name="additionalOptionsPreset"
                    type="checkbox"
                    onChange={e => this.handleAddtionalOptionPresetUpdate(e.target.checked, key)}
                  />
                );
              })}
            </div>
          }
          {this.props.fieldConfig.propertyTypeUseFilters &&
            <p className="mb-2">
              <strong>Optional:</strong>&nbsp;
              Include the following use types in the property type filter.  When using these options, do not also
              add the 'Property Use' filter.
            </p>
          }
          {this.props.fieldConfig.propertyTypeUseFilters &&
            <div className="mb-3">
              {map(this.props.fieldConfig.propertyTypeUseFilters, (label, key) => {
                return (
                  <Form.Check
                    checked={this.isPropertyTypeUseFilterSelected(key)}
                    id={`${idPrefix}-property-type-${key}`}
                    key={key}
                    label={label}
                    name="propertyTypeUseFilter"
                    type="checkbox"
                    onChange={e => this.handleAPropertyTypeUseFilterUpdate(e.target.checked, key)}
                  />
                );
              })}
            </div>
          }

          {this.allowAllSelectedLabel() &&
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    All selected Label
                    {this.allSelectedLabelInfoIcon()}
                  </Form.Label>
                  <Form.Control
                    name="allSelectedLabel"
                    value={this.props.filterModel.allSelectedLabel || ''}
                    onChange={this.handleUpdate}
                  />
                </Form.Group>
              </Col>
            </Row>
          }

          <hr />

          <h6 className="mt-0 mb-4">Display Rules</h6>
          <p>Specify when you want this filter to display</p>

          {this.showStatusCondition() &&
            <>
              <Row>
                <Col md={12}>
                  <label className="form-label mb-2">Property Status</label>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={6}>
                  <Form.Select
                    name="saleOrLease"
                    value={this.props.filterModel.saleOrLease || ''}
                    onChange={this.handleUpdate}
                  >
                    <option value=''>Any Status</option>
                    <option value='sale'>Sale</option>
                    <option value='lease'>Lease</option>
                  </Form.Select>
                </Col>
                <Col md={6}>
                  { this.singleSelectLabel('saleOrLease', 'status') }
                </Col>
              </Row>
            </>
          }

          {this.showPropertyTypeCondition() &&
            <>
              <Row>
                <Col md={12}>
                  <label className="form-label mb-2">Property Type</label>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={6}>
                  <Select
                    isMulti
                    isSearchable={false}
                    name="propertyTypes"
                    options={this.propertyTypeOptions()}
                    value={this.propertyTypeValues()}
                    onChange={this.handlePropertyTypeUpdate}
                  />
                </Col>
                <Col md={6}>
                  { this.propertyTypeLabel() }
                </Col>
              </Row>
            </>
          }
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" type="button" onClick={this.props.onClose}>Update</button>
        </Modal.Footer>
      </Modal>
    );
  }
}
